import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, PencilSimple, Phone, NavigationArrow, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MobileTeamNotes } from "@/components/MobileTeamNotes";

const photoSrc = (p, w = 320) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/photos/${p.storage_path}?auth=${localStorage.getItem("sw_token")}&w=${w}`;

export default function MobileService() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const setStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/service-calls/${id}/status`, { status });
      setC(data);
      toast.success(status === "Done" ? "Service call marked done" : `Status: ${status}`);
    } catch {
      toast.error("Could not update status");
    }
    setBusy(false);
  };

  useEffect(() => {
    api.get(`/service-calls/${id}`).then(({ data }) => setC(data)).catch(() => setErr("Service call not found"));
  }, [id]);

  if (err) return <div className="p-6 text-sm text-red-300" data-testid="mobile-service-error">{err}</div>;
  if (!c) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;
  const photos = (c.photos || []).filter((p) => !p.is_deleted);
  const Row = ({ label, children }) =>
    children ? (
      <div className="flex gap-3 py-2 text-sm">
        <span className="w-24 shrink-0 text-[10px] uppercase tracking-widest text-zinc-500 pt-0.5">{label}</span>
        <span className="min-w-0 flex-1 text-zinc-100">{children}</span>
      </div>
    ) : null;

  return (
    <div className="px-4 pb-28 pt-4" data-testid="mobile-service-detail">
      <button type="button" onClick={() => navigate(-1)} data-testid="mobile-service-back" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500">
        <ArrowLeft size={13} weight="bold" /> Back
      </button>
      <div className="mt-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-red-300">
            <Wrench size={13} weight="bold" /> Service · {c.code}
          </div>
          <h1 className="mt-1 text-xl font-bold text-zinc-100">{c.issue_type}</h1>
          <div className="text-sm text-zinc-300">{c.customer_name}</div>
        </div>
        <Link to={`/projects/service/${id}/edit`} data-testid="mobile-service-edit" className="inline-flex h-9 shrink-0 items-center gap-1 rounded-lg border border-zinc-700 px-3 text-[11px] font-semibold uppercase tracking-widest text-zinc-300">
          <PencilSimple size={13} weight="bold" /> Edit
        </Link>
      </div>

      <div className="mt-4 rounded-2xl border border-zinc-800 bg-[#0F172A]/60 px-4 py-2 divide-y divide-zinc-800/70">
        <Row label="Status">{c.status}</Row>
        <Row label="Priority">{c.priority}</Row>
        <Row label="When">{[c.scheduled_date, c.time || c.window].filter(Boolean).join(" · ")}</Row>
        <Row label="Crew">{c.crew}</Row>
        <Row label="Job">{c.job_code}</Row>
        <Row label="Address">{c.address}</Row>
        <Row label="Details">{c.description && <span className="whitespace-pre-wrap">{c.description}</span>}</Row>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2" data-testid="mobile-service-actions">
        {c.status !== "On site" && c.status !== "Done" && (
          <button type="button" data-testid="mobile-service-onsite" disabled={busy} onClick={() => setStatus("On site")} className="h-12 rounded-xl border border-[#c39e6d]/60 bg-[#c39e6d]/15 text-xs font-bold uppercase tracking-widest text-[#c39e6d] disabled:opacity-50">
            Mark on site
          </button>
        )}
        {c.status !== "Done" && (
          <button type="button" data-testid="mobile-service-done" disabled={busy} onClick={() => setStatus("Done")} className="h-12 rounded-xl bg-emerald-600 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50">
            Mark done
          </button>
        )}
        {c.status === "Done" && (
          <button type="button" data-testid="mobile-service-reopen" disabled={busy} onClick={() => setStatus("Scheduled")} className="col-span-2 h-12 rounded-xl border border-zinc-700 text-xs font-bold uppercase tracking-widest text-zinc-300 disabled:opacity-50">
            Done ✓ — tap to reopen
          </button>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {c.address && (
          <a data-testid="mobile-service-directions" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(c.address)}`} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-zinc-700 text-xs font-semibold uppercase tracking-widest text-zinc-200">
            <NavigationArrow size={15} weight="bold" /> Directions
          </a>
        )}
        {c.customer_phone && (
          <a data-testid="mobile-service-call" href={`tel:${c.customer_phone}`} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-zinc-700 text-xs font-semibold uppercase tracking-widest text-zinc-200">
            <Phone size={15} weight="bold" /> Call
          </a>
        )}
      </div>

      {c.project_id && <div className="mt-3"><MobileTeamNotes projectId={c.project_id} testid={`mobile-service-${id}`} /></div>}

      <div className="mt-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Photos ({photos.length})</div>
        {photos.length === 0 ? (
          <p className="mt-2 text-xs text-zinc-500">No photos yet — add them from the stop card on the calendar.</p>
        ) : (
          <div className="mt-2 grid grid-cols-3 gap-2">
            {photos.map((p) => (
              <a key={p.id} href={photoSrc(p, 1600)} target="_blank" rel="noreferrer" data-testid={`mobile-service-photo-${p.id}`}>
                <img src={photoSrc(p)} alt={p.caption || ""} className="aspect-square w-full rounded-lg object-cover" />
                {p.caption && <div className="mt-0.5 truncate text-[10px] text-zinc-400">{p.caption}</div>}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
